import { action, computed, makeObservable, observable } from 'mobx';
import { TKiosk, kioskCast } from '../../../types/kiosks';
import { defaultEditableKiosk } from '../constants/kiosks';
import { IKioskErrorMessages } from '../types/kioskEdit';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';

class KioskEditStore {
  constructor() {
    makeObservable(this);
  }

  @observable public loading: LoadingStatus = LoadingStatus.Init;
  @observable public isDataEdited: boolean = false;

  @observable public editableKiosk: TKiosk = defaultEditableKiosk;
  @observable public errorMessages: IKioskErrorMessages = { branch: null, ip: null, password: null, port: null };

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loading);
  }

  @action public init = (kioskId?: string) => {
    this.loading = LoadingStatus.Init;
    this.isDataEdited = false;
    this.editableKiosk = defaultEditableKiosk;
    this.errorMessages = { branch: null, ip: null, password: null, port: null };

    if (kioskId) this.fetchKiosk(kioskId);
  };

  @action private fetchKiosk = async (id: string) => {
    try {
      this.loading = LoadingStatus.Loading;

      const kiosk = await client.get(`v4/kiosks/${id}`);

      this.editableKiosk = kioskCast(kiosk);

      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'kiosksStore fetchKiosk' });
    }
  };

  @action public changeEditableKiosk = (data: Partial<TKiosk>) => {
    this.editableKiosk = { ...this.editableKiosk, ...data };
    this.isDataEdited = true;
  };

  @action public onSubmit = () => {
    this.validateBeforeSave();

    if (this.editableKiosk.id) {
      return this.updateKiosk(this.editableKiosk.id);
    } else {
      return this.createKiosk();
    }
  };

  private get _preparedApiData() {
    return {
      password: this.editableKiosk.password,
      language: this.editableKiosk.language,
      branch_id: this.editableKiosk.branchId,
      terminal_id: this.editableKiosk.terminalId,
      ip: this.editableKiosk.ip,
      port: this.editableKiosk.port,
      kiosk_terminal_multiplier: this.editableKiosk.kiosk_terminal_multiplier,
    };
  }

  @action private createKiosk = async () => {
    try {
      this.loading = LoadingStatus.Refreshing;

      await client.post('v4/kiosks', this._preparedApiData);

      this.isDataEdited = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'kiosksStore createKiosk' });
    }
  };

  @action private updateKiosk = async (kioskId: string) => {
    try {
      this.loading = LoadingStatus.Refreshing;

      await client.put(`v4/kiosks/${kioskId}`, this._preparedApiData);

      this.isDataEdited = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'kiosksStore updateKiosk' });
    }
  };

  @action public validateBeforeSave = () => {
    this.validatePassword();
    this.validateBranch();
    this.validateIp();
    this.validatePort();

    return Object.values(this.errorMessages).every((msg) => msg === null);
  };

  @action public validatePassword = () => {
    this.errorMessages.password = !this.editableKiosk.password ? 'required_field_error' : null;
  };

  @action public validateBranch = () => {
    this.errorMessages.branch = !this.editableKiosk.branchId ? 'required_field_error' : null;
  };

  @action public validateIp = () => {
    const IP_PATTERN = /^([0-9]{1,3}\.){3}[0-9]{1,3}$/;
    if (!this.editableKiosk.ip) return;
    this.errorMessages.ip = !IP_PATTERN.test(this.editableKiosk.ip) ? 'invalid_ip' : null;
  };

  @action public validatePort = () => {
    const port = parseInt(this.editableKiosk.port, 10);
    this.errorMessages.port = port > 65535 ? 'port_is_invalid' : null;
  };
}

const kioskEditStore = new KioskEditStore();

export default kioskEditStore;
